import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface DocumentationRequirement {
  id: string;
  title: string;
  description: string | null;
  content_expected: string | null;
  sort_order: number;
}

interface DocumentationSection {
  id: string;
  section_number: number;
  title: string;
  description: string | null;
  is_optional: boolean;
  requirements: DocumentationRequirement[];
}

export const useGSDocumentationRequirements = (standardType: string) => {
  const [sections, setSections] = useState<DocumentationSection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRequirements = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // First get sections
        const { data: sectionsData, error: sectionsError } = await supabase
          .from('standard_documentation_sections')
          .select('*')
          .eq('standard_type', standardType)
          .order('section_number', { ascending: true });

        if (sectionsError) throw sectionsError;

        // Then get requirements for each section
        const sectionsWithRequirements = await Promise.all(
          sectionsData.map(async (section) => {
            const { data: requirements, error: reqError } = await supabase
              .from('standard_documentation_requirements')
              .select('*')
              .eq('section_id', section.id)
              .order('sort_order', { ascending: true });

            if (reqError) throw reqError;

            return {
              ...section,
              requirements: requirements || []
            };
          })
        );

        setSections(sectionsWithRequirements);
      } catch (err) {
        console.error('Error fetching documentation requirements:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch requirements');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRequirements();
  }, [standardType]);

  return {
    sections,
    isLoading,
    error,
  };
};